import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { api } from "../../lib/api";
import { IconButton, SectionHeader, StatTile, StateDot, Tag, Icon as DsIcon } from "./ds";
import "./week-recap.css";

// Popup „Ako prebiehal týždeň" — sedem dní Po–Ne so stavom, získaná medaila (alebo koľko
// dní k nej chýbalo) a stručné čísla týždňa. Otvára ho pondelkový oznam (deep-link
// prehlad:tyzden:<pondelok>) aj popup Spoľahlivosť.
//
// Všetko počíta server (reliability.week_recap) — presne tými istými funkciami ako medailu
// v odznakoch, takže popup nikdy nemôže tvrdiť „7 zo 7" pri chýbajúcej zlatej medaile.
// Tu sa nič neprepočítava, len sa to pomenúva.
const MEDAL = {
  gold: { icon: "medal", label: "Zlatý týždeň", desc: "Všetkých 7 dní si dotiahol/la — celý týždeň bez zaváhania." },
  silver: { icon: "medal2", label: "Strieborný týždeň", desc: "6 zo 7 dní splnených. Jeden deň ušiel a to je úplne v poriadku." },
};
// StateDot pozná done/miss/skip/off/muted; „nebolo naplánované" je v jeho slovníku `muted`
const DOT = { done: "done", miss: "miss", off: "muted" };

const dni = (n) => (n === 1 ? "deň" : n >= 2 && n <= 4 ? "dni" : "dní");

// posun o týždeň nad ISO dátumom pondelka (bez knižnice; poludnie drží prechod cez letný čas)
function shiftWeek(iso, weeks) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

export function WeekRecapModal({ week, onClose }) {
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  // `cur` = týždeň, ktorý sa práve pozerá; `week` z propu je len východiskový bod
  // (z oznamu chodí konkrétny pondelok, z Prehľadu nič = posledný uzavretý týždeň)
  const [cur, setCur] = useState(week || "");
  useEffect(() => { setCur(week || ""); }, [week]);
  useEffect(() => {
    let alive = true;
    setRec(null); setErr("");
    api("/api/week-recap" + (cur ? "?week=" + encodeURIComponent(cur) : ""))
      .then((r) => { if (alive) setRec(r); })
      .catch(() => { if (alive) setErr("Týždeň sa nepodarilo načítať. Skús to o chvíľu znova."); });
    return () => { alive = false; };
  }, [cur]);
  // Hlavička popupu. Bez medaily povedz konkrétne, čo k nej chýbalo — to je jediná vec, ktorú
  // sa z týždňa dá naozaj naučiť (a nedá sa to prečítať ako výčitka). Tri rôzne dôvody, prečo
  // medaila nie je, sa nesmú miešať: inak by týždeň bez jediného splnenia hlásil „7 zo 7"
  // (voľné dni sa počítajú ako splnené) a zároveň „chýbali 3 dni".
  const m = rec && rec.medal ? MEDAL[rec.medal] : null;
  let head = null;
  if (rec && !m) {
    if (rec.done_days === 0 && rec.missed_days === 0) {
      head = { title: "Týždeň bez záznamu",
        desc: "Nemal/a si nič naplánované ani splnené — z tohto týždňa sa nedá nič vyčítať." };
    } else if (rec.done_days < rec.min_done_days) {
      head = { title: `${rec.done_days} ${dni(rec.done_days)} so splnením`,
        desc: `Na týždennú medailu treba aspoň ${rec.min_done_days} dni, v ktorých si niečo splnil/a.` };
    } else {
      head = { title: `${rec.fulfilled} zo 7 dní splnených`,
        desc: rec.to_silver === 1
          ? "Jediný deň ťa delil od strieborného týždňa."
          : `Do strieborného týždňa chýbalo ${rec.to_silver} ${dni(rec.to_silver)}.` };
    }
  }
  return (
    <Modal center title="Ako prebiehal týždeň" icon="calendar" maxWidth={420}
      subtitle={rec ? rec.range : null} onClose={onClose}>
      {err && <div className="bz-wrec__note">{err}</div>}
      {!rec && !err && <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><div className="spin" /></div>}
      {rec && (
        <>
          {/* listovanie týždňami — dozadu po týždeň vzniku účtu, dopredu po posledný
              uzavretý (bežiaci týždeň sa nerekapituluje, ešte sa nedohral) */}
          <div className="bz-wrec__nav">
            <IconButton icon="back" size="sm" variant="ghost" aria-label="Predchádzajúci týždeň"
              disabled={!rec.has_prev} onClick={() => setCur(shiftWeek(rec.week, -1))} />
            <span className="bz-wrec__navlabel">{rec.range}</span>
            <IconButton icon="chevron" size="sm" variant="ghost" aria-label="Nasledujúci týždeň"
              disabled={rec.is_last_week} onClick={() => setCur(shiftWeek(rec.week, 1))} />
          </div>

          <div className="bz-wrec__medal">
            <span className={`bz-wrec__medalicon${m ? "" : " bz-wrec__medalicon--none"}`}>
              <DsIcon name={m ? m.icon : "calendar"} size={26} color="currentColor" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="bz-wrec__medaltitle">{m ? m.label : head.title}</div>
              <div className="bz-wrec__medalsub">{m ? m.desc : head.desc}</div>
            </div>
          </div>

          <div className="bz-wrec__days">
            {rec.days.map((d) => (
              <div key={d.day} className={`bz-wrec__day bz-wrec__day--${d.status}`}>
                <span className="bz-wrec__dow">{d.dow}</span>
                <StateDot state={DOT[d.status] || "off"} size={20} />
                <span className="bz-wrec__date">{d.date}.</span>
              </div>
            ))}
          </div>
          <div className="bz-wrec__legend">
            <span className="bz-wrec__legenditem"><StateDot state="done" size={11} />Splnené</span>
            <span className="bz-wrec__legenditem"><StateDot state="miss" size={11} />Vynechané</span>
            <span className="bz-wrec__legenditem"><StateDot state="muted" size={11} />Nebolo naplánované</span>
          </div>

          <SectionHeader label="Čísla týždňa" />
          <div className="bz-statgrid2">
            <StatTile accent="var(--stat-growth)" icon={<DsIcon name="check" size={24} color="currentColor" />}
              value={`${rec.fulfilled}/7`} label="Splnené dni"
              sub={rec.off_days ? `${rec.off_days} ${dni(rec.off_days)} bez plánu` : "všetky dni s plánom"}
              pct={(100 * rec.fulfilled) / 7} />
            <StatTile accent="var(--stat-sun)" icon={<DsIcon name="coins" size={24} color="currentColor" />}
              value={rec.points} label="Body za týždeň"
              sub={`${rec.completions}× splnený návyk`} />
            <StatTile accent="var(--stat-growth)" icon={<DsIcon name="target" size={24} color="currentColor" />}
              value={rec.perfect_days} label="Perfektné dni" sub="všetko naplánované hotové" />
            <StatTile accent="var(--stat-flame)" icon={<DsIcon name="flame" size={24} color="currentColor" />}
              value={rec.best_run} label="Najdlhšia séria" sub="najviac splnených dní po sebe" />
          </div>

          {(rec.gold_weeks > 0 || rec.silver_weeks > 0) && (
            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Tag tone="brand" size="xs" icon="medal">Zlaté týždne: {rec.gold_weeks}</Tag>
              <Tag tone="neutral" size="xs" icon="medal2">Strieborné: {rec.silver_weeks}</Tag>
            </div>
          )}
          <div className="bz-wrec__note">
            Zlatý týždeň = všetkých 7 dní splnených, strieborný = 6 zo 7. Deň, keď si nemal/a nič
            naplánované, sa počíta ako splnený — presne ako pri sérii.
          </div>
        </>
      )}
    </Modal>
  );
}
